// =============================================================================
// useAuth — Convenience hook for auth state and actions
// =============================================================================
// Provides a clean interface for components to access auth state.
// Wraps the Zustand store with derived helpers.
// =============================================================================

import { useAuthStore } from '@/lib/auth-store';

/**
 * Hook providing auth state and actions.
 *
 * @example
 * ```tsx
 * function ProfileMenu() {
 *   const { user, isAuthenticated, logout } = useAuth();
 *   if (!isAuthenticated) return null;
 *   return <button onClick={logout}>{user?.email}</button>;
 * }
 * ```
 */
export function useAuth() {
  const store = useAuthStore();

  return {
    // State
    user: store.user,
    status: store.status,
    error: store.error,

    // Derived — 'idle' and 'loading' both block UI until auth resolves
    isAuthenticated: store.status === 'authenticated',
    isLoading: store.status === 'idle' || store.status === 'loading',
    isInitialized: store.status !== 'idle',

    // Actions
    login: store.login,
    register: store.register,
    logout: store.logout,
    switchOrganization: store.switchOrganization,
    initialize: store.initialize,
    setUser: store.setUser,
    clearError: store.clearError,
  };
}
